/* eslint-disable max-lines-per-function */

import { Redirect, Route, Switch } from 'react-router';

import { NotFoundPanel } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { visitPaths } from '~libs.desktop.shared.paths';
import { WaitlistPanel } from '~libs.desktop.pages.waitlist';

import { AppointmentsPanel } from './appointment/AppointmentsPanel';
import { AppointmentsPanelLoader } from './appointment/AppointmentsPanel.loader';
import { VisitDependencies, VisitDependenciesContextProvider } from './dependencies';
import { PrescriptionVisitsPanel } from './prescriptionVisits/PrescriptionVisitsPanel';
import { PrescriptionVisitsPanelLoader } from './prescriptionVisits/PrescriptionVisitsPanel.loader';
import { VisitPanel } from './visit/VisitPanel';
import { PrescriptionVisitPanel } from './visit/PrescriptionVisitPanel';
import { VisitHistoryPage } from './visitHistory/VisitHistoryPage';
import { CalendarPanelLoader } from './calendar/CalendarPanel.loader';
import { CalendarPanel } from './calendar/CalendarPanel';

export type VisitModuleRouterOptions = VisitDependencies;

export const useVisitModuleRouter = (options: VisitModuleRouterOptions): JSX.Element => {
   return (
      <Route
         path={[
            visitPaths.APPOINTMENTS_PATH,
            visitPaths.VISIT_PATH,
            visitPaths.PRESCRIPTION_VISIT_PATH,
            visitPaths.VISIT_HISTORY_PATH,
            visitPaths.CALENDAR_PATH,
            visitPaths.COMPLETE_PATIENT_DATA_PATH,
            visitPaths.PRESCRIPTION_VISITS_PATH,
         ]}
      >
         <VisitDependenciesContextProvider
            accountConfigurationVerificationEventInterceptor={options.accountConfigurationVerificationEventInterceptor}
         >
            <Switch>
               {protectFeature(
                  'searchingAppointments',
                  <Route path={visitPaths.APPOINTMENTS_PATH}>
                     <AppointmentsPanelLoader>
                        {({ currentDoctor, doctors, patientGroups }) => (
                           <AppointmentsPanel
                              currentDoctor={currentDoctor}
                              doctors={doctors}
                              patientGroups={patientGroups}
                           />
                        )}
                     </AppointmentsPanelLoader>
                  </Route>
               )}
               {protectFeature(
                  ['searchingAppointments'],
                  <Route path={visitPaths.PRESCRIPTION_VISITS_PATH}>
                     <PrescriptionVisitsPanelLoader>
                        {({ currentDoctor, doctors, patientGroups, onlinePaymentsIntegrationExists }) => (
                           <PrescriptionVisitsPanel
                              currentDoctor={currentDoctor}
                              doctors={doctors}
                              patientGroups={patientGroups}
                              onlinePaymentsIntegrationExists={onlinePaymentsIntegrationExists}
                           />
                        )}
                     </PrescriptionVisitsPanelLoader>
                  </Route>
               )}
               {protectFeature(
                  'creatingAppointments',
                  <Route path={visitPaths.PRESCRIPTION_VISIT_PATH}>
                     <PrescriptionVisitPanel />
                  </Route>
               )}
               {protectFeature(
                  'creatingAppointments',
                  <Route path={visitPaths.VISIT_PATH}>
                     <VisitPanel />
                  </Route>
               )}
               {protectFeature(
                  'searchingMedicalData',
                  <Route path={visitPaths.VISIT_HISTORY_PATH}>
                     <VisitHistoryPage />
                  </Route>
               )}
               {protectFeature(
                  'searchingAppointments',
                  <Route exact path={visitPaths.CALENDAR_PATH}>
                     <CalendarPanelLoader>
                        {({ practitioners }) => <CalendarPanel practitioners={practitioners} />}
                     </CalendarPanelLoader>
                  </Route>
               )}
               {/* TODO DREGAB-27120 */}
               {protectFeature(
                  ['searchingAppointments', 'experimental'],
                  <Route exact path={visitPaths.CALENDAR_WAITLIST_PATH}>
                     <WaitlistPanel />
                  </Route>
               )}
               <Route>
                  <NotFoundPanel />
               </Route>
            </Switch>
         </VisitDependenciesContextProvider>
      </Route>
   );
};

export const useRedirectToAppointmentsList = (): JSX.Element | null => {
   return protectFeature('searchingAppointments', <Redirect to={visitPaths.getAppointmentsListPath()} />, null);
};
